import type { CustomFieldDefinitionId } from "@repo/app-types/entities/customField";
import type { DateRange } from "@repo/app-types/entities/dateRange";
import type {
  MetadataFieldId,
  SignalId,
} from "@repo/app-types/entities/signal";
import {
  findMultiSelectOptionId,
  MultiSelect,
} from "@repo/ui/Select/multiSelect";
import { SimpleSelect } from "@repo/ui/Select/simpleSelect";
import { useNow } from "@repo/ui/timestamps/timestampContext";
import { Duration } from "luxon";
import { type FC, type ReactNode, useMemo, useState } from "react";
import { useReportDataStreamQuery } from "../../api/entities/performanceReport.js";
import {
  useCustomFieldDefinitionSelectLabel,
  useCustomFieldDefinitionSelectOptions,
} from "../../components/customFieldDefinitionSelect.js";
import { useSignalMetadataFieldSelectOptions } from "../../components/signalMetadataFieldSelect.js";
import { useSignalSelectOptions } from "../../components/signalSelect.js";
import {
  buildTeamPerformancePageFilters,
  dateRangeToISORange,
} from "../filters/buildFilterQueryArgs.js";
import { DateRangeFilterItem } from "../filters/dateRangeFilterItem.js";
import { useFilters } from "../filters/filters.js";
import { useCompanyDefaultDateRange } from "../filters/useCompanyDefaultDateRange.js";
import type { ReportRenderProps } from "./baseReport.js";

const FALLBACK_DEFAULT_PERIOD: DateRange = {
  length: Duration.fromObject({ days: 7 }),
  end: undefined,
};

export interface InsuranceUpsellOutcomeReportProps {
  children: (props: ReportRenderProps) => ReactNode;
}

export const InsuranceUpsellOutcomeReport: FC<
  InsuranceUpsellOutcomeReportProps
> = (props) => {
  const [isSettingsExpanded, setIsSettingsExpanded] = useState(false);
  const onSettingsToggle = () => {
    setIsSettingsExpanded((isExpanded) => !isExpanded);
  };

  const [signalId, signalSetting] = useSignalSetting();
  const [initialAmountFieldId, initialAmountFieldSetting] =
    useSignalMetadataFieldSetting(
      signalId,
      "Initial amount field",
      /initial.+amount/i,
    );
  const [initialTermFieldId, initialTermFieldSetting] =
    useSignalMetadataFieldSetting(
      signalId,
      "Initial term field",
      /initial.+term/i,
    );
  const [recommendedAmountFieldId, recommendedAmountFieldSetting] =
    useSignalMetadataFieldSetting(
      signalId,
      "Recommended amount field",
      /recommended.+amount/i,
    );
  const [recommendedTermFieldId, recommendedTermFieldSetting] =
    useSignalMetadataFieldSetting(
      signalId,
      "Recommended term field",
      /recommended.+term/i,
    );
  const [
    coverageRequestCustomFieldDefinitionId,
    coverageRequestCustomFieldDefinitionSetting,
  ] = useCustomFieldDefinitionSetting(
    "Coverage request custom field",
    /CRI\s+date/i,
  );
  const [revenueCustomFieldDefinitionId, revenueCustomFieldDefinitionSetting] =
    useCustomFieldDefinitionSetting(
      "Revenue custom field",
      /revenue|in-?force\s+premium/i,
    );
  const [pivotCustomFieldDefinitionId, pivotCustomFieldDefinitionSetting] =
    useCustomFieldDefinitionSetting("Pivot custom field", /carrier/i);

  const now = useNow();
  const endOfYesterday = useMemo(
    () => now.minus({ days: 1 }).endOf("day"),
    [now],
  );

  const companyDefaultPeriod = useCompanyDefaultDateRange();
  const [customPeriod, setPeriod] = useState<DateRange | undefined>(undefined);
  const period: DateRange =
    customPeriod ?? companyDefaultPeriod ?? FALLBACK_DEFAULT_PERIOD;

  // No `outcome` / `playbookConditions` filter keys: those are
  // playbook-scoped and this report has no playbook in scope (the
  // analysis spans gradings across whichever playbook ingested them).
  const [
    { teammateGroupIds, customFields, totalRecordingDuration },
    renderFilters,
  ] = useFilters([
    "teammateGroupIds",
    "customFields",
    "totalRecordingDuration",
  ]);

  const { data, isFetching, refetch } = useReportDataStreamQuery({
    type: "insurance-upsell-outcome",
    periodLengthDays: Math.round(period.length.as("days")),
    // Default to the last full day (i.e. yesterday) to avoid showing a weird trend due to partial data
    periodEnd: period.end ?? endOfYesterday,
    // Use the user's time zone
    timeZone: now.zone,
    signalId,
    initialAmountFieldId,
    initialTermFieldId,
    recommendedAmountFieldId,
    recommendedTermFieldId,
    coverageRequestCustomFieldDefinitionId,
    revenueCustomFieldDefinitionId,
    pivotCustomFieldDefinitionId,
    filters: buildTeamPerformancePageFilters({
      playbookId: null,
      teammateGroupIds: teammateGroupIds ?? null,
      outcome: null,
      customFields,
      playbookConditions: [],
      totalRecordingDuration,
      signalId: null,
      signalMetadataFields: [],
      selectedDateRange: dateRangeToISORange(now, {
        length: period.length,
        end: period.end ?? endOfYesterday,
      }),
      selectedDateRangeTimeZone: now.zoneName,
    }),
  });

  return props.children({
    title:
      "How coverage and term upsells affect inforce premium per opportunity",
    isScopedToPlaybook: false,
    isSettingsExpanded,
    onSettingsToggle,
    settings: (
      <>
        {signalSetting}
        {initialAmountFieldSetting}
        {initialTermFieldSetting}
        {recommendedAmountFieldSetting}
        {recommendedTermFieldSetting}
        {coverageRequestCustomFieldDefinitionSetting}
        {revenueCustomFieldDefinitionSetting}
        {pivotCustomFieldDefinitionSetting}
      </>
    ),
    renderFilterBar: (options) =>
      renderFilters({
        isLoading: isFetching,
        trailingItems: (
          <DateRangeFilterItem
            hasBasisPicker={false}
            value={period}
            onChange={setPeriod}
          />
        ),
        rightActions: options?.rightActions,
      }),
    onRefresh: refetch,
    state: data?.state,
  });
};

function useCustomFieldDefinitionSetting(
  placeholder: string,
  defaultRegExp: RegExp,
) {
  const options = useCustomFieldDefinitionSelectOptions();

  // Find a custom field that looks like it might be related to revenue
  const defaultId = findMultiSelectOptionId(options, (option) =>
    defaultRegExp.test(option.label),
  );

  // Allow the revenue field to be customized
  const [customId, setCustomId] = useState<CustomFieldDefinitionId | undefined>(
    undefined,
  );

  // Use the customized revenue field or the default
  const selectedId = customId ?? defaultId;

  const label = useCustomFieldDefinitionSelectLabel(options, selectedId);

  // TODO: Introduce a single select with sections
  const setting = (
    <MultiSelect
      label={label}
      placeholder={placeholder}
      sections={options}
      value={selectedId ? [selectedId] : []}
      onChange={(ids) => {
        setCustomId(ids.at(-1));
      }}
    />
  );

  return [selectedId, setting] as const;
}

function useSignalSetting() {
  const options = useSignalSelectOptions();
  const defaultId = options.find((option) =>
    /upsell/i.test(option.label),
  )?.value;
  const [customId, setCustomId] = useState<SignalId | undefined>(undefined);
  const selectedId = customId ?? defaultId;

  const setting = (
    <SimpleSelect
      label="Upsell signal"
      isLabelHidden
      placeholder="Upsell signal"
      options={options}
      value={selectedId ?? null}
      onChange={(value) => {
        setCustomId(value ?? undefined);
      }}
    />
  );

  return [selectedId, setting] as const;
}

function useSignalMetadataFieldSetting(
  signalId: SignalId | undefined,
  placeholder: string,
  defaultRegExp: RegExp,
) {
  const options = useSignalMetadataFieldSelectOptions(signalId);
  const defaultId = options.find((option) =>
    defaultRegExp.test(option.label),
  )?.value;
  const [customId, setCustomId] = useState<MetadataFieldId | undefined>(
    undefined,
  );
  const selectedId = customId ?? defaultId;

  const setting = (
    <SimpleSelect
      label={placeholder}
      isLabelHidden
      placeholder={placeholder}
      options={options}
      value={selectedId ?? null}
      onChange={(value) => {
        setCustomId(value ?? undefined);
      }}
    />
  );

  return [selectedId, setting] as const;
}
